import React, { useState } from 'react';
import { employer } from '../data/mockData';

const STEPS = [
  {
    key: 'auth',
    num: '🔑',
    label: 'Auth',
    fullLabel: 'Authentication',
    status: 'done',
    description: 'HMAC-SHA256 request signing. Salt fetched via GET /auth/salt/{RequestUser}. Password hashed with SHA1, combined into RequestHash.',
    outputs: [],
    apis: ['GET /auth/salt/{RequestUser}'],
    duration: '1.2s',
  },
  {
    key: 'step1',
    num: '1',
    label: 'Step 1',
    fullLabel: 'Fetch Employer Config',
    status: 'done',
    description: 'Fetches all employer-level configuration: earning codes, deduction codes, tax codes, client profile, departments, work locations, and employee list.',
    outputs: ['employer_config.json', 'employee_list.json'],
    apis: ['GET /api/v1/employer/Earnings', 'GET /api/v1/employer/deductions', 'GET /api/v1/employer/taxes', 'GET /api/v1/employer/clientprofile', 'GET /api/v1/employer/departments', 'GET /api/v1/employees'],
    duration: '3.1s',
  },
  {
    key: 'step2',
    num: '2',
    label: 'Step 2',
    fullLabel: 'Employee Profile Fetcher',
    status: 'done',
    description: 'Iterates all 8 employees and fetches full profile + earnings elections + deduction elections. 1 employee returned 404 (new hire GUID not yet in system).',
    outputs: ['employee_profiles.json'],
    apis: ['GET /api/v1/employees/{id}', 'GET /api/v1/employee/{id}/Earnings', 'GET /api/v1/employee/{id}/deductions', 'GET /api/v1/employee/{id}/Taxes', 'GET /api/v1/employee/{id}/DirectDeposits'],
    duration: '7.8s',
  },
  {
    key: 'step3',
    num: '3',
    label: 'Step 3',
    fullLabel: 'Extractor V3 (AI Parse)',
    status: 'done',
    description: 'Claude Extractor V3 Skill invoked with source files (EML email, CSV timeclock, XLSX tips) + employer config + employee profiles. Output is a fully populated Enhanced Workbook v8.',
    outputs: ['ACM-2024-001_PrimePay_Enhanced_Workbook_v8_POPULATED.xlsx'],
    apis: ['Claude API (internal)'],
    duration: '37s',
  },
  {
    key: 'step4',
    num: '4',
    label: 'Step 4',
    fullLabel: 'XLSX → JSON Convert',
    status: 'done',
    description: 'Deterministic conversion of workbook sheets to structured JSON payloads. Maps earning/deduction columns to confirmed PrimePay API field names. Skips GUID_REQUIRED rows.',
    outputs: ['earnings_payload.json', 'deductions_payload.json', 'flags_report.json', 'batch_metadata.json'],
    apis: ['None — deterministic transform'],
    duration: '0.4s',
  },
  {
    key: 'step5',
    num: '5',
    label: 'Step 5',
    fullLabel: 'Validation Layer',
    status: 'blocked',
    description: '8 validation rules executed. 3 CRITICAL flags found — load is blocked until resolved. 2 HIGH warnings raised. Operator action required on Carlos Vega GUID and gross reconciliation variance.',
    outputs: ['validation_report.json'],
    apis: ['None — rule engine'],
    duration: '0.1s',
  },
  {
    key: 'step6',
    num: '6',
    label: 'Step 6',
    fullLabel: 'API Loader',
    status: 'partial',
    description: 'Idempotent PUT-based loader. Ran for 7/8 employees. Carlos Vega skipped (CRITICAL flags). All other earnings and deductions loaded via PUT. RowStamp refresh not required this run.',
    outputs: ['load_results.json', 'reconciliation_report.json'],
    apis: ['GET /api/v1/employee/{id}/Earnings', 'PUT /api/v1/employee/{id}/Earnings', 'GET /api/v1/employee/{id}/deductions', 'PUT /api/v1/employee/{id}/deductions'],
    duration: '41s',
  },
];

export default function PipelineRunner() {
  const [selected, setSelected] = useState('step1');
  const active = STEPS.find(s => s.key === selected);

  const statusColor = st =>
    st === 'done' ? 'done' : st === 'blocked' ? 'blocked' : st === 'partial' ? 'warning' : '';

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h2>Pipeline Runner</h2>
          <p>Run ID: {employer.lastRunId} · Pay period {employer.payPeriodStart} – {employer.payPeriodEnd}</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary btn-sm">🔁 Dry Run</button>
          <button className="btn btn-primary btn-sm">▶ Re-run Pipeline</button>
        </div>
      </div>

      {/* Stepper */}
      <div className="pipeline-track">
        {STEPS.map((step, i) => (
          <div key={step.key} className="pipeline-step-wrap">
            <div className="pipeline-step" onClick={() => setSelected(step.key)}>
              <div className={`step-circle ${statusColor(step.status)} ${selected === step.key ? 'active' : ''}`}
                style={selected === step.key ? { boxShadow: '0 0 0 4px rgba(28,61,90,0.15)' } : {}}>
                {step.status === 'done' ? '✓' : step.status === 'blocked' ? '✕' : step.status === 'partial' ? '◑' : step.num}
              </div>
              <span className={`step-label ${step.status === 'done' ? 'done-label' : step.status === 'blocked' ? 'blocked-label' : ''}`}>
                {step.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`step-connector ${step.status === 'done' ? 'done-line' : step.status === 'blocked' ? 'blocked-line' : ''}`} />
            )}
          </div>
        ))}
      </div>

      {/* Detail Panel */}
      {active && (
        <div className="card section-gap">
          <div className="card-header">
            <span className={`badge badge-${active.status === 'done' ? 'success' : active.status === 'blocked' ? 'critical' : 'high'}`}>
              {active.status.toUpperCase()}
            </span>
            <h2>{active.fullLabel}</h2>
            <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ink-4)' }}>
              ⏱ {active.duration}
            </span>
          </div>
          <div className="card-body">
            <div className="grid-2" style={{ gap: 28 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--ink-4)', marginBottom: 8 }}>Description</div>
                <p style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.65 }}>{active.description}</p>

                {active.outputs.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--ink-4)', margin: '20px 0 8px' }}>Output Artifacts</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {active.outputs.map(o => (
                        <div key={o} style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          background: 'var(--success-bg)', padding: '7px 12px', borderRadius: 6,
                          fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--success)'
                        }}>
                          <span>📄</span> {o}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--ink-4)', marginBottom: 8 }}>API Calls</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {active.apis.map(a => (
                    <div key={a} style={{
                      background: 'var(--primary-pale)', padding: '6px 12px', borderRadius: 5,
                      fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--primary)'
                    }}>
                      {a}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step selector list */}
      <div className="card">
        <div className="card-header"><h2>All Steps Summary</h2></div>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Step</th><th>Component</th><th>Type</th><th>Status</th><th>Duration</th><th>Output Files</th>
              </tr>
            </thead>
            <tbody>
              {STEPS.map(s => (
                <tr key={s.key} style={{ cursor: 'pointer' }} onClick={() => setSelected(s.key)}>
                  <td><span className="badge badge-primary">{s.label}</span></td>
                  <td style={{ fontWeight: 600 }}>{s.fullLabel}</td>
                  <td>
                    {s.key === 'step3'
                      ? <span className="badge badge-high">AI (Claude)</span>
                      : <span className="badge badge-neutral">Deterministic</span>}
                  </td>
                  <td>
                    <span className={`badge badge-${s.status === 'done' ? 'success' : s.status === 'blocked' ? 'critical' : 'high'}`}>
                      {s.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="mono">{s.duration}</td>
                  <td style={{ color: 'var(--ink-3)', fontSize: 12 }}>
                    {s.outputs.join(', ') || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
